import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Mail, Calendar, Ticket, MessageSquare, FileText, CreditCard, BookOpen, Mic, Terminal, Code2, Database, Monitor, Camera, Headphones, Server, Link2, Shield, ShieldCheck, ShieldAlert, Star, Search, X, Check, Eye, EyeOff } from 'lucide-react';
type Runtime = 'cli' | 'container' | 'api' | 'oauth';
type Domain = 'communication' | 'data' | 'documents' | 'meetings' | 'agents' | 'media' | 'infrastructure' | 'web';
type SkillStatus = 'connected' | 'available';
interface Permission {
  label: string;
  level: 'read' | 'write' | 'execute';
  granted: boolean;
}
interface Skill {
  id: string;
  name: string;
  domain: Domain;
  runtime: Runtime;
  icon: React.FC<{
    size?: number;
    className?: string;
  }>;
  status: SkillStatus;
  openClaw?: boolean;
  stars?: number;
  description: string;
  permissions?: Permission[];
  credentials?: {
    label: string;
    value: string;
    secret: boolean;
  }[];
  outputRouting?: string[];
}
const DOMAIN_LABELS: Record<Domain, string> = {
  communication: 'Communication',
  data: 'Data',
  documents: 'Documents',
  meetings: 'Meetings',
  agents: 'AI Agents',
  media: 'Media',
  infrastructure: 'Infrastructure',
  web: 'Web'
};
const RUNTIME_LABELS: Record<Runtime, string> = {
  cli: 'CLI',
  container: 'Container',
  api: 'API',
  oauth: 'OAuth'
};
const SKILLS: Skill[] = [{
  id: 'gmail',
  name: 'Gmail',
  domain: 'communication',
  runtime: 'oauth',
  icon: Mail,
  status: 'connected',
  description: 'Email sync, send, and label management',
  credentials: [{
    label: 'Account',
    value: 'matt@contextloops.com',
    secret: false
  }],
  outputRouting: ['messages', 'data'],
  permissions: [{
    label: 'Read emails',
    level: 'read',
    granted: true
  }, {
    label: 'Send emails',
    level: 'write',
    granted: true
  }, {
    label: 'Manage labels',
    level: 'write',
    granted: true
  }]
}, {
  id: 'slack',
  name: 'Slack',
  domain: 'communication',
  runtime: 'oauth',
  icon: MessageSquare,
  status: 'available',
  description: 'Messages, channels, and thread sync'
}, {
  id: 'whatsapp',
  name: 'WhatsApp',
  domain: 'communication',
  runtime: 'container',
  icon: MessageSquare,
  status: 'available',
  description: 'Send and receive via WhatsApp Business API'
}, {
  id: 'imessage',
  name: 'iMessage',
  domain: 'communication',
  runtime: 'cli',
  icon: MessageSquare,
  status: 'available',
  description: 'Bridge iMessage on macOS via local relay'
}, {
  id: 'jira',
  name: 'Jira',
  domain: 'data',
  runtime: 'oauth',
  icon: Ticket,
  status: 'connected',
  description: 'Issues, projects, sprints',
  credentials: [{
    label: 'Site',
    value: 'contextloops.atlassian.net',
    secret: false
  }],
  outputRouting: ['data', 'actions'],
  permissions: [{
    label: 'Read issues',
    level: 'read',
    granted: true
  }, {
    label: 'Edit issues',
    level: 'write',
    granted: true
  }, {
    label: 'Create issues',
    level: 'write',
    granted: true
  }]
}, {
  id: 'quickbooks',
  name: 'QuickBooks',
  domain: 'data',
  runtime: 'oauth',
  icon: CreditCard,
  status: 'available',
  description: 'Invoices, transactions, vendors'
}, {
  id: 'google-calendar',
  name: 'Google Calendar',
  domain: 'data',
  runtime: 'oauth',
  icon: Calendar,
  status: 'connected',
  description: 'Events and attendees',
  credentials: [{
    label: 'Account',
    value: 'matt@contextloops.com',
    secret: false
  }],
  outputRouting: ['data'],
  permissions: [{
    label: 'Read events',
    level: 'read',
    granted: true
  }, {
    label: 'Create events',
    level: 'write',
    granted: true
  }]
}, {
  id: 'rest-api',
  name: 'REST API',
  domain: 'data',
  runtime: 'api',
  icon: Database,
  status: 'connected',
  description: 'Connect any REST endpoint as a data source',
  credentials: [{
    label: 'Endpoints',
    value: '3 configured',
    secret: false
  }],
  outputRouting: ['data']
}, {
  id: 'turso',
  name: 'Turso CLI',
  domain: 'data',
  runtime: 'cli',
  icon: Database,
  status: 'available',
  openClaw: true,
  stars: 342,
  description: 'Query libSQL databases from the agent'
}, {
  id: 'confluence',
  name: 'Confluence',
  domain: 'documents',
  runtime: 'oauth',
  icon: FileText,
  status: 'connected',
  description: 'Pages and spaces sync',
  credentials: [{
    label: 'Site',
    value: 'contextloops.atlassian.net',
    secret: false
  }],
  outputRouting: ['library'],
  permissions: [{
    label: 'Read pages',
    level: 'read',
    granted: true
  }, {
    label: 'Add comments',
    level: 'write',
    granted: true
  }]
}, {
  id: 'google-drive',
  name: 'Google Drive',
  domain: 'documents',
  runtime: 'oauth',
  icon: BookOpen,
  status: 'available',
  description: 'Documents, spreadsheets, and files'
}, {
  id: 'openutter',
  name: 'OpenUtter',
  domain: 'meetings',
  runtime: 'cli',
  icon: Mic,
  status: 'available',
  openClaw: true,
  stars: 128,
  description: 'Join Google Meet, capture transcript and screenshots',
  permissions: [{
    label: 'Browser automation',
    level: 'execute',
    granted: false
  }, {
    label: 'Write transcripts',
    level: 'write',
    granted: false
  }, {
    label: 'Take screenshots',
    level: 'write',
    granted: false
  }, {
    label: 'Network access',
    level: 'execute',
    granted: false
  }]
}, {
  id: 'claude-code',
  name: 'Claude Code',
  domain: 'agents',
  runtime: 'api',
  icon: Terminal,
  status: 'connected',
  description: 'Spawn coding agents for development tasks',
  credentials: [{
    label: 'Model',
    value: 'claude-opus-4-6',
    secret: false
  }, {
    label: 'API key',
    value: 'sk-ant-api03-9f1a...4b6c8d0e',
    secret: true
  }],
  outputRouting: ['actions'],
  permissions: [{
    label: 'Read files',
    level: 'read',
    granted: true
  }, {
    label: 'Write files',
    level: 'write',
    granted: true
  }, {
    label: 'Execute commands',
    level: 'execute',
    granted: true
  }]
}, {
  id: 'codex',
  name: 'Codex',
  domain: 'agents',
  runtime: 'api',
  icon: Code2,
  status: 'available',
  description: 'OpenAI code generation agents'
}, {
  id: 'whisper',
  name: 'Whisper',
  domain: 'media',
  runtime: 'cli',
  icon: Headphones,
  status: 'available',
  openClaw: true,
  stars: 2100,
  description: 'Transcribe audio and video files to text'
}, {
  id: 'yt-dlp',
  name: 'yt-dlp',
  domain: 'media',
  runtime: 'cli',
  icon: Monitor,
  status: 'available',
  openClaw: true,
  stars: 890,
  description: 'Download video and audio from URLs'
}, {
  id: 'coolify',
  name: 'Coolify',
  domain: 'infrastructure',
  runtime: 'api',
  icon: Server,
  status: 'connected',
  description: 'Deploy, restart, and monitor apps',
  credentials: [{
    label: 'URL',
    value: 'https://coolify.contextloops.com',
    secret: false
  }, {
    label: 'Token',
    value: 'cl_4f2a...8d0e',
    secret: true
  }],
  outputRouting: ['actions'],
  permissions: [{
    label: 'Read status',
    level: 'read',
    granted: true
  }, {
    label: 'Deploy apps',
    level: 'execute',
    granted: true
  }, {
    label: 'Restart services',
    level: 'execute',
    granted: true
  }]
}, {
  id: 'gh-cli',
  name: 'GitHub CLI',
  domain: 'infrastructure',
  runtime: 'cli',
  icon: Code2,
  status: 'available',
  openClaw: true,
  stars: 450,
  description: 'Create issues, PRs, check CI status'
}, {
  id: 'playwright',
  name: 'Playwright',
  domain: 'web',
  runtime: 'cli',
  icon: Camera,
  status: 'available',
  openClaw: true,
  stars: 670,
  description: 'Capture full-page screenshots of any URL'
}, {
  id: 'link-preview',
  name: 'Link Preview',
  domain: 'web',
  runtime: 'api',
  icon: Link2,
  status: 'available',
  description: 'Fetch metadata and preview for any URL'
}];
export function SkillsView() {
  const [search, setSearch] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [installingId, setInstallingId] = useState<string | null>(null);
  if (installingId) {
    const skill = SKILLS.find(s => s.id === installingId);
    if (skill) return <SkillInstall skill={skill} onDone={() => setInstallingId(null)} onBack={() => setInstallingId(null)} data-id="element-1613" />;
  }
  if (viewingId) {
    const skill = SKILLS.find(s => s.id === viewingId);
    if (skill) return <SkillDetail skill={skill} onBack={() => setViewingId(null)} onInstall={() => {
      setViewingId(null);
      setInstallingId(skill.id);
    }} data-id="element-1614" />;
  }
  const filtered = SKILLS.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.domain.includes(search.toLowerCase()));
  const domains = Object.keys(DOMAIN_LABELS) as Domain[];
  const grouped = domains.map(d => ({
    domain: d,
    label: DOMAIN_LABELS[d],
    skills: filtered.filter(s => s.domain === d)
  })).filter(g => g.skills.length > 0);
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1615">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-1616">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-6" data-id="element-1617">Skills</h1>
        <div className="relative mb-8" data-id="element-1618"><Search size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8a8680]/40" data-id="element-1619" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search skills" className="w-full bg-transparent text-[#e8e4df] pl-6 pr-4 py-2 text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 focus:border-[#8a8680]/30 transition-colors" data-id="element-1620" /></div>
        <div className="flex flex-col gap-10" data-id="element-1621">
          {grouped.map(({
          domain,
          label,
          skills
        }) => <section key={domain} data-id="element-1622">
              <h2 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-1623">{label}</h2>
              <div className="flex flex-col gap-3" data-id="element-1624">
                {skills.map((skill, i) => {
              const Icon = skill.icon;
              return <motion.button key={skill.id} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.3,
                delay: i * 0.03
              }} onClick={() => skill.status === 'available' && skill.openClaw ? setInstallingId(skill.id) : setViewingId(skill.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-1625">
                      <div className="flex items-center gap-4" data-id="element-1626">
                        <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-1627"><Icon size={18} className={skill.status === 'connected' ? 'text-[#8a8680]' : 'text-[#8a8680]/50'} strokeWidth={1.5} data-id="element-1628" /></div>
                        <div className="flex-1 min-w-0" data-id="element-1629">
                          <div className="flex items-center gap-2" data-id="element-1630">
                            <span className={`text-[15px] ${skill.status === 'connected' ? 'text-[#e8e4df]' : 'text-[#e8e4df]/70'}`} data-id="element-1631">{skill.name}</span>
                            <span className="text-[10px] text-[#8a8680]/40" data-id="element-1632">{RUNTIME_LABELS[skill.runtime]}</span>
                            {skill.openClaw && <span className="text-[10px] text-[#4a9a9a]/60" data-id="element-1633">OpenClaw</span>}
                            {skill.stars && <span className="flex items-center gap-0.5 text-[10px] text-[#8a8680]/40" data-id="element-1634"><Star size={8} data-id="element-1635" />{skill.stars >= 1000 ? `${(skill.stars / 1000).toFixed(1)}k` : skill.stars}</span>}
                          </div>
                        </div>
                        {skill.status === 'connected' && <div className="w-1.5 h-1.5 rounded-full bg-[#4a9a9a] shrink-0" data-id="element-1636" />}
                        {skill.status === 'available' && <span className="text-[12px] text-[#8a8680]/40 group-hover:text-[#8a8680] transition-colors shrink-0" data-id="element-1637">{skill.openClaw ? 'Install' : 'Connect'}</span>}
                      </div>
                    </motion.button>;
            })}
              </div>
            </section>)}
        </div>
      </div>
    </div>;
}
function SkillDetail({
  skill,
  onBack,
  onInstall
}: {
  skill: Skill;
  onBack: () => void;
  onInstall: () => void;
}) {
  const Icon = skill.icon;
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1638">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-1639">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-1640"><ArrowLeft size={14} data-id="element-1641" /><span className="text-[13px]" data-id="element-1642">Skills</span></button>
        <header className="mb-8 flex items-center gap-4" data-id="element-1643">
          <div className="w-12 h-12 rounded-[14px] bg-[#252530] flex items-center justify-center" data-id="element-1644"><Icon size={22} className="text-[#8a8680]" strokeWidth={1.5} data-id="element-1645" /></div>
          <div data-id="element-1646">
            <div className="flex items-center gap-2" data-id="element-1647"><h1 className="text-[20px] font-medium text-[#e8e4df]" data-id="element-1648">{skill.name}</h1>{skill.status === 'connected' && <div className="w-1.5 h-1.5 rounded-full bg-[#4a9a9a]" data-id="element-1649" />}</div>
            <div className="flex items-center gap-3 mt-0.5" data-id="element-1650"><span className="text-[12px] text-[#8a8680]/50" data-id="element-1651">{RUNTIME_LABELS[skill.runtime]}</span>{skill.openClaw && <span className="text-[12px] text-[#4a9a9a]/60" data-id="element-1652">OpenClaw</span>}{skill.stars && <span className="flex items-center gap-1 text-[12px] text-[#8a8680]/40" data-id="element-1653"><Star size={10} data-id="element-1654" />{skill.stars}</span>}</div>
          </div>
        </header>
        <p className="text-[14px] text-[#8a8680] leading-[1.6] mb-8" data-id="element-1655">{skill.description}</p>
        {skill.permissions && skill.permissions.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1656">
            <h3 className="text-[12px] text-[#8a8680]/50 mb-3" data-id="element-1657">Permissions</h3>
            {skill.permissions.map((perm, i) => <div key={i} className="flex items-center justify-between py-2" data-id="element-1658"><div className="flex items-center gap-2" data-id="element-1659">{perm.granted ? <ShieldCheck size={13} className="text-[#4a9a9a]/70" data-id="element-1660" /> : <Shield size={13} className="text-[#8a8680]/40" data-id="element-1661" />}<span className="text-[13px] text-[#e8e4df]/80" data-id="element-1662">{perm.label}</span></div><span className="text-[11px] text-[#8a8680]/40" data-id="element-1663">{perm.level}</span></div>)}
          </div>}
        {skill.credentials && skill.credentials.length > 0 && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1664">
            <h3 className="text-[12px] text-[#8a8680]/50 mb-3" data-id="element-1665">Credentials</h3>
            {skill.credentials.map((cred, i) => <div key={i} className="flex items-center justify-between py-2" data-id="element-1666"><span className="text-[13px] text-[#8a8680]" data-id="element-1667">{cred.label}</span>{cred.secret ? <div className="flex items-center gap-2" data-id="element-1668"><span className="text-[13px] text-[#e8e4df] font-mono" data-id="element-1669">{revealed[i] ? cred.value : `${cred.value.slice(0, 8)}${'\u2022'.repeat(6)}`}</span><button onClick={() => setRevealed(prev => ({
              ...prev,
              [i]: !prev[i]
            }))} className="text-[#8a8680]/40 hover:text-[#8a8680]" data-id="element-1670">{revealed[i] ? <EyeOff size={12} data-id="element-1671" /> : <Eye size={12} data-id="element-1672" />}</button></div> : <span className="text-[13px] text-[#e8e4df]" data-id="element-1673">{cred.value}</span>}</div>)}
          </div>}
        {skill.outputRouting && <div className="bg-[#1c1c22] rounded-[16px] p-5 mb-4" data-id="element-1674"><h3 className="text-[12px] text-[#8a8680]/50 mb-3" data-id="element-1675">Output routes to</h3><div className="flex items-center gap-3" data-id="element-1676">{skill.outputRouting.map(r => <span key={r} className="text-[12px] text-[#8a8680] capitalize" data-id="element-1677">{r}</span>)}</div></div>}
        {skill.status === 'connected' && <button className="text-[13px] text-[#8a8680]/50 hover:text-[#8a8680] transition-colors mt-4" data-id="element-1678">Disconnect</button>}
        {skill.status === 'available' && <button onClick={onInstall} className="text-[14px] text-[#4a9a9a]/80 hover:text-[#4a9a9a] transition-colors mt-4" data-id="element-1679">{skill.openClaw ? 'Install' : 'Connect'}</button>}
      </div>
    </motion.div>;
}
type ScanPhase = 'downloading' | 'scanning' | 'permissions' | 'complete';
function SkillInstall({
  skill,
  onDone,
  onBack
}: {
  skill: Skill;
  onDone: () => void;
  onBack: () => void;
}) {
  const [phase, setPhase] = useState<ScanPhase>('downloading');
  const [scanResults, setScanResults] = useState([{
    label: 'Package signature',
    status: 'checking' as const
  }, {
    label: 'Dependencies',
    status: 'checking' as const
  }, {
    label: 'Network access',
    status: 'checking' as const
  }, {
    label: 'File system access',
    status: 'checking' as const
  }, {
    label: 'Process execution',
    status: 'checking' as const
  }]);
  const [permissions, setPermissions] = useState<Permission[]>(skill.permissions || [{
    label: 'Read files',
    level: 'read',
    granted: false
  }, {
    label: 'Write to output directory',
    level: 'write',
    granted: false
  }, {
    label: 'Network access',
    level: 'execute',
    granted: false
  }, {
    label: 'Execute binaries',
    level: 'execute',
    granted: false
  }]);
  const Icon = skill.icon;
  useEffect(() => {
    if (phase === 'downloading') {
      const t = setTimeout(() => setPhase('scanning'), 1200);
      return () => clearTimeout(t);
    }
    if (phase === 'scanning') {
      const timers: ReturnType<typeof setTimeout>[] = [];
      scanResults.forEach((_, i) => {
        timers.push(setTimeout(() => {
          setScanResults(prev => prev.map((r, j) => j === i ? {
            ...r,
            status: (i === 2 ? 'warning' : 'safe') as 'safe' | 'warning'
          } : r));
        }, 600 + i * 500));
      });
      timers.push(setTimeout(() => setPhase('permissions'), 600 + scanResults.length * 500 + 400));
      return () => timers.forEach(clearTimeout);
    }
  }, [phase]);
  const togglePermission = (i: number) => setPermissions(prev => prev.map((p, j) => j === i ? {
    ...p,
    granted: !p.granted
  } : p));
  const allGranted = permissions.every(p => p.granted);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-1680">
      <div className="max-w-[480px] mx-auto w-full" data-id="element-1681">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-10" data-id="element-1682"><ArrowLeft size={14} data-id="element-1683" /><span className="text-[13px]" data-id="element-1684">Skills</span></button>
        <div className="flex flex-col items-center gap-6 mb-10" data-id="element-1685">
          <div className="w-14 h-14 rounded-[16px] bg-[#252530] flex items-center justify-center" data-id="element-1686"><Icon size={26} className="text-[#8a8680]" strokeWidth={1.5} data-id="element-1687" /></div>
          <div className="text-center" data-id="element-1688">
            <h1 className="text-[18px] font-medium text-[#e8e4df]" data-id="element-1689">{skill.name}</h1>
            <div className="flex items-center justify-center gap-3 mt-1" data-id="element-1690">{skill.openClaw && <span className="text-[12px] text-[#4a9a9a]/60" data-id="element-1691">OpenClaw</span>}{skill.stars && <span className="flex items-center gap-1 text-[12px] text-[#8a8680]/40" data-id="element-1692"><Star size={10} data-id="element-1693" />{skill.stars}</span>}<span className="text-[12px] text-[#8a8680]/40" data-id="element-1694">{RUNTIME_LABELS[skill.runtime]}</span></div>
          </div>
        </div>

        <AnimatePresence mode="wait" data-id="element-1695">
          {phase === 'downloading' && <motion.div key="dl" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center gap-4 py-8" data-id="element-1696">
              <div className="w-5 h-5 border-2 border-[#4a9a9a]/30 border-t-[#4a9a9a] rounded-full animate-spin" data-id="element-1697" />
              <p className="text-[14px] text-[#8a8680]/60" data-id="element-1698">Downloading</p>
            </motion.div>}
          {phase === 'scanning' && <motion.div key="scan" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1699">
              <div className="flex items-center gap-2 mb-5" data-id="element-1700"><Shield size={14} className="text-[#8a8680]/60" data-id="element-1701" /><span className="text-[12px] text-[#8a8680]/50" data-id="element-1702">Security scan</span></div>
              <div className="flex flex-col gap-4" data-id="element-1703">
                {scanResults.map((result, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.1
            }} className="flex items-center justify-between" data-id="element-1704">
                    <span className="text-[14px] text-[#e8e4df]/80" data-id="element-1705">{result.label}</span>
                    {result.status === 'checking' && <div className="w-3 h-3 border border-[#8a8680]/30 border-t-[#8a8680] rounded-full animate-spin" data-id="element-1706" />}
                    {result.status === 'safe' && <ShieldCheck size={14} className="text-[#4a9a9a]/70" data-id="element-1707" />}
                    {result.status === 'warning' && <ShieldAlert size={14} className="text-amber-400/70" data-id="element-1708" />}
                  </motion.div>)}
              </div>
            </motion.div>}
          {phase === 'permissions' && <motion.div key="perms" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1709">
              <div className="bg-[#1c1c22] rounded-[16px] p-6 mb-6" data-id="element-1710">
                <div className="flex items-center gap-2 mb-1" data-id="element-1711"><Shield size={14} className="text-[#8a8680]/60" data-id="element-1712" /><span className="text-[12px] text-[#8a8680]/50" data-id="element-1713">Scan complete</span></div>
                <div className="flex items-center gap-3 mt-3 mb-5" data-id="element-1714">{scanResults.map((r, i) => <div key={i} className={`w-2 h-2 rounded-full ${r.status === 'safe' ? 'bg-[#4a9a9a]/60' : 'bg-amber-400/60'}`} data-id="element-1715" />)}</div>
                {scanResults.some(r => r.status === 'warning') && <p className="text-[13px] text-amber-400/60 leading-[1.5] mb-6" data-id="element-1716">This skill requests network access. It will be sandboxed to allowed endpoints only.</p>}
                <h3 className="text-[12px] text-[#8a8680]/50 mb-4" data-id="element-1717">Grant permissions</h3>
                {permissions.map((perm, i) => <button key={i} onClick={() => togglePermission(i)} className="flex items-center justify-between w-full py-3 group" data-id="element-1718">
                    <div className="flex items-center gap-3" data-id="element-1719">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${perm.granted ? 'bg-[#4a9a9a]/60 border-[#4a9a9a]/60' : 'border-[#8a8680]/30 group-hover:border-[#8a8680]/60'}`} data-id="element-1720">{perm.granted && <Check size={9} className="text-[#141418]" data-id="element-1721" />}</div>
                      <span className="text-[14px] text-[#e8e4df]/80" data-id="element-1722">{perm.label}</span>
                    </div>
                    <span className="text-[11px] text-[#8a8680]/40" data-id="element-1723">{perm.level}</span>
                  </button>)}
              </div>
              <div className="flex items-center justify-center gap-6" data-id="element-1724">
                <button onClick={() => {
              setPhase('complete');
              setTimeout(onDone, 800);
            }} className={`text-[14px] transition-colors ${allGranted ? 'text-[#4a9a9a]/80 hover:text-[#4a9a9a]' : 'text-[#8a8680]/30'}`} data-id="element-1725">Install</button>
                <button onClick={onBack} className="text-[14px] text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors" data-id="element-1726">Cancel</button>
              </div>
            </motion.div>}
          {phase === 'complete' && <motion.div key="done" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center gap-4 py-8" data-id="element-1727">
              <ShieldCheck size={22} className="text-[#4a9a9a]/70" data-id="element-1728" />
              <p className="text-[14px] text-[#8a8680]/60" data-id="element-1729">Installed</p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}